import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { db } from '../../firebase';
import { ref, onValue } from 'firebase/database';
import { 
    FaArrowLeft, 
    FaSort,
    FaFileAlt
} from 'react-icons/fa';

const AuditInterestedWorkList = () => {
    const navigate = useNavigate();
    const location = useLocation();
    
    // Determine if we are looking at Engineers or Societies based on the path
    const isSociety = location.pathname.includes('society-interested');
    const department = isSociety ? 'society' : 'subhe';

    const [works, setWorks] = useState([]);
    const [registrations, setRegistrations] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);

        // Fetch All Works
        const worksRef = ref(db, 'ekamwatapzp/WorkNames');
        const unsubWorks = onValue(worksRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                let flattenedWorks = [];
                Object.entries(data).forEach(([key, val]) => {
                    if (val && typeof val === 'object') {
                        if (val.schemeName) {
                            flattenedWorks.push({ id: key, meetingNumber: val.meetingNumber || "General", ...val });
                        } else {
                            Object.entries(val).forEach(([id, value]) => {
                                flattenedWorks.push({ id, meetingNumber: key, ...value });
                            });
                        }
                    }
                });
                setWorks(flattenedWorks);
            }
        });

        // Fetch Registrations (Both Engineer and Society)
        const engRef = ref(db, 'ekamwatapzp/EngineerRegistrations');
        const socRef = ref(db, 'ekamwatapzp/SocietyRegistrations');

        let engData = {}, socData = {};
        let engDone = false, socDone = false;

        const checkRegDone = () => {
            if (engDone && socDone) {
                const allRegs = [
                    ...Object.entries(engData).map(([key, val]) => ({ ...val, _key: key })),
                    ...Object.entries(socData).map(([key, val]) => ({ ...val, _key: key }))
                ];
                setRegistrations(allRegs);
                setLoading(false);
            }
        };

        const unsubEng = onValue(engRef, (snapshot) => {
            engData = snapshot.val() || {};
            engDone = true;
            checkRegDone();
        });

        const unsubSoc = onValue(socRef, (snapshot) => {
            socData = snapshot.val() || {};
            socDone = true;
            checkRegDone();
        });

        return () => {
            unsubWorks();
            unsubEng();
            unsubSoc();
        };
    }, []);

    // Filter and Process Data
    const filteredWorks = useMemo(() => {
        const deptMap = { 'subhe': 'सुबे', 'society': 'मजूर' };
        return works.filter(work => {
            return !department || work.sharedwork === deptMap[department];
        });
    }, [works, department]);

    // Pre-calculate work interest counts
    const workInterestMap = useMemo(() => {
        const counts = {};
        registrations.forEach(reg => {
            if (reg.work_payment) {
                Object.entries(reg.work_payment).forEach(([meetingNum, paymentData]) => {
                    const selected = paymentData.selectedWorks || [];
                    selected.forEach(workStr => {
                        const workNum = workStr.split(' - ')[0];
                        const key = `${meetingNum}_${workNum}`;
                        counts[key] = (counts[key] || 0) + 1;
                    });
                });
            }
        });
        return counts;
    }, [registrations]);

    const displayData = useMemo(() => {
        return filteredWorks.map(work => ({
            ...work,
            interestedCount: workInterestMap[`${work.meetingNumber}_${work.workNumber}`] || 0
        })).filter(w => w.interestedCount > 0); // Only show works with interests
    }, [filteredWorks, workInterestMap]);

    const totalInterestSum = useMemo(() => {
        return displayData.reduce((acc, curr) => acc + curr.interestedCount, 0);
    }, [displayData]);

    if (loading) {
        return (
            <div className="p-20 flex flex-col items-center justify-center space-y-4">
                <div className="animate-spin text-[#085e53] text-4xl">
                    <FaSort />
                </div>
                <p className="text-gray-400 font-bold animate-pulse uppercase tracking-widest text-xs tracking-widest">माहिती लोड होत आहे...</p>
            </div>
        );
    }

    return (
        <div className="p-4 md:p-8 bg-gray-50/50 min-h-screen font-marathi">
            {/* Header / Breadcrumb Section */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4 px-2">
                <div className="flex items-center gap-4">
                    <button 
                        onClick={() => navigate(-1)}
                        className="p-2.5 bg-white rounded-xl shadow-sm border border-gray-100 text-gray-400 hover:text-[#085e53] transition active:scale-95"
                    >
                        <FaArrowLeft />
                    </button>
                    <h1 className="text-2xl font-bold text-gray-800 tracking-tight">इंटरेस्टेड वर्क लिस्ट</h1>
                </div>
                <div className="flex items-center gap-2 text-[12px] font-bold text-gray-400 uppercase tracking-wider">
                    सांगली जिल्हा परिषद / <span className="text-gray-600">ऑडिट विभाग / इंटरेस्टेड वर्क लिस्ट</span>
                </div>
            </div>

            {/* Main Content Card */}
            <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead className="bg-[#f8f9fa] border-b border-gray-200">
                            <tr className="text-[13px] font-black text-gray-600 uppercase tracking-tight">
                                <th className="px-6 py-5 border-r border-gray-100 w-16 text-center">#</th>
                                <th className="px-6 py-5 border-r border-gray-100 w-24">वर्क नंबर</th>
                                <th className="px-6 py-5 border-r border-gray-100 min-w-[300px]">कामाचे नाव</th>
                                <th className="px-6 py-5 border-r border-gray-100 w-32">कामाची रक्कम</th>
                                <th className="px-6 py-5 border-r border-gray-100 w-44">टोटल इंटरेस्टेड</th>
                                <th className="px-6 py-5 text-center w-32">अतिरिक्त कागदपत्रे</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {displayData.map((item, index) => (
                                <tr key={item.id} className="hover:bg-emerald-50/40 transition-colors group">
                                    <td className="px-6 py-5 border-r border-gray-50 text-center font-bold text-gray-400">{index + 1}</td>
                                    <td className="px-6 py-5 border-r border-gray-50 font-bold text-gray-700">{item.workNumber}</td>
                                    <td className="px-6 py-5 border-r border-gray-50 font-bold text-gray-800 leading-relaxed text-sm">
                                        {item.workName}
                                    </td>
                                    <td className="px-6 py-5 border-r border-gray-50 font-bold text-gray-600">{item.tenderAmount}</td>
                                    <td className="px-6 py-5 border-r border-gray-50">
                                        <div className="flex items-center gap-2">
                                            <span className="font-bold text-gray-800">{item.interestedCount}</span>
                                            <span className="text-gray-300">-</span>
                                            <button 
                                                onClick={() => navigate(`/audit/reports/interested-detail/${item.id}`)}
                                                className="text-[#085e53] hover:text-[#064a41] font-bold text-sm underline decoration-emerald-200 underline-offset-4 flex items-center gap-2 transition-all"
                                            >
                                                इंटरेस्टेड अर्ज
                                            </button>
                                        </div>
                                    </td>
                                    <td className="px-6 py-5 text-center">
                                        <button 
                                            onClick={() => navigate(`/audit/work-info/details/${item.meetingNumber}/${item.id}`)}
                                            className="p-2 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-[#085e53] transition-all"
                                        >
                                            <FaFileAlt size={16} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {displayData.length === 0 && (
                                <tr>
                                    <td colSpan="6" className="px-6 py-20 text-center text-gray-400 font-bold italic">
                                        कोणतीही माहिती सापडली नाही.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                        <tfoot className="bg-[#f8f9fa] border-t border-gray-200">
                            <tr>
                                <td colSpan="4" className="px-6 py-5 text-right font-black text-gray-700 text-lg pr-12">
                                    Total Interest
                                </td>
                                <td className="px-6 py-5 text-left font-black text-gray-800 text-lg">
                                    {totalInterestSum}
                                </td>
                                <td className="px-6 py-5"></td>
                            </tr>
                        </tfoot>
                    </table>
                </div>

                {/* Print Button at Bottom Center */}
                <div className="p-10 flex justify-center">
                    <button 
                        onClick={() => window.print()}
                        className="bg-[#085e53] hover:bg-[#064a41] text-white px-10 py-2.5 rounded shadow-md font-bold transition transform active:scale-95 no-print"
                    >
                        Print
                    </button>
                </div>
            </div>

            <style>{`
                .font-marathi {
                    font-family: 'Hind', sans-serif;
                }
                @import url('https://fonts.googleapis.com/css2?family=Hind:wght@400;500;600;700&display=swap');
            `}</style>
        </div>
    );
};

export default AuditInterestedWorkList;
